import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AuthBridge } from "./context/AuthBridge";
import { ToastProvider } from "./context/ToastContext";
import { AppLayout } from "./components/layout/AppLayout";
import { LoginScreen } from "./pages/auth/LoginScreen";
import { ForbiddenPage } from "./pages/auth/ForbiddenPage";
import { AccessRequestPage } from "./pages/auth/AccessRequestPage";
import { ReviewAccessRequestsPage } from "./pages/auth/ReviewAccessRequestsPage";
import { ROUTES } from "./routes";

import { DashboardHomePage } from "./pages/DashboardHomePage";
import { SecurityMonitoringPage } from "./pages/security/SecurityMonitoringPage";
import { AuditLogPage } from "./pages/security/AuditLogPage";
import { CompliancePage } from "./pages/security/CompliancePage";
import { ThemeAnalyticsPage } from "./pages/analytics/ThemeAnalyticsPage";
import { SubscriptionAnalyticsPage } from "./pages/analytics/SubscriptionAnalyticsPage";
import { FeatureAdoptionPage } from "./pages/analytics/FeatureAdoptionPage";
import { DormantAccountsPage } from "./pages/analytics/DormantAccountsPage";
import { CostMonitoringPage } from "./pages/analytics/CostMonitoringPage";
import { SentimentAnalysisPage } from "./pages/analytics/SentimentAnalysisPage";
import { MobileAnalyticsPage } from "./pages/analytics/MobileAnalyticsPage";
import { BloomAnalyticsPage } from "./pages/analytics/BloomAnalyticsPage";
import { SupportTicketsPage } from "./pages/operations/SupportTicketsPage";
import { PerformanceMonitoringPage } from "./pages/operations/PerformanceMonitoringPage";
import { AppInsightsPage } from "./pages/operations/AppInsightsPage";
import { UserManagementPage } from "./pages/management/UserManagementPage";
import { FeatureFlagsPage } from "./pages/management/FeatureFlagsPage";
import { SystemManagementPage } from "./pages/management/SystemManagementPage";
import { CustomReportingPage } from "./pages/reports/CustomReportingPage";

function AuthedApp() {
  const { forbidden, jitRequired } = useAuth();
  if (forbidden) return <ForbiddenPage />;
  // Admin group membership is confirmed but this session has no active
  // JIT grant (see apiClient.js's jit_access_required handling) -- route to
  // the request-access flow instead of the normal routes, same gating shape
  // as `forbidden` above.
  if (jitRequired) return <AccessRequestPage />;

  return (
    <AppLayout>
      <Routes>
        <Route path={ROUTES.HOME} element={<DashboardHomePage />} />
        <Route path={ROUTES.ACCESS_REQUEST} element={<AccessRequestPage />} />
        <Route path={ROUTES.REVIEW_ACCESS_REQUESTS} element={<ReviewAccessRequestsPage />} />

        <Route path={ROUTES.SECURITY_MONITORING} element={<SecurityMonitoringPage />} />
        <Route path={ROUTES.AUDIT_LOG} element={<AuditLogPage />} />
        <Route path={ROUTES.COMPLIANCE} element={<CompliancePage />} />

        <Route path={ROUTES.THEME_ANALYTICS} element={<ThemeAnalyticsPage />} />
        <Route path={ROUTES.SUBSCRIPTION_ANALYTICS} element={<SubscriptionAnalyticsPage />} />
        <Route path={ROUTES.FEATURE_ADOPTION} element={<FeatureAdoptionPage />} />
        <Route path={ROUTES.DORMANT_ACCOUNTS} element={<DormantAccountsPage />} />
        <Route path={ROUTES.COST_MONITORING} element={<CostMonitoringPage />} />
        <Route path={ROUTES.SENTIMENT_ANALYSIS} element={<SentimentAnalysisPage />} />
        <Route path={ROUTES.MOBILE_ANALYTICS} element={<MobileAnalyticsPage />} />
        <Route path={ROUTES.BLOOM_ANALYTICS} element={<BloomAnalyticsPage />} />

        <Route path={ROUTES.SUPPORT_TICKETS} element={<SupportTicketsPage />} />
        <Route path={ROUTES.PERFORMANCE_MONITORING} element={<PerformanceMonitoringPage />} />
        <Route path={ROUTES.APP_INSIGHTS} element={<AppInsightsPage />} />

        <Route path={ROUTES.USER_MANAGEMENT} element={<UserManagementPage />} />
        <Route path={ROUTES.FEATURE_FLAGS} element={<FeatureFlagsPage />} />
        <Route path={ROUTES.SYSTEM_MANAGEMENT} element={<SystemManagementPage />} />

        <Route path={ROUTES.CUSTOM_REPORTING} element={<CustomReportingPage />} />

        <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
      </Routes>
    </AppLayout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthBridge />
      <ToastProvider>
        <AuthenticatedTemplate>
          <BrowserRouter>
            <AuthedApp />
          </BrowserRouter>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <LoginScreen />
        </UnauthenticatedTemplate>
      </ToastProvider>
    </AuthProvider>
  );
}
